import React from "react";
import PropTypes from "prop-types";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

import Constants from "Helpers/Constants";

import { Grid, MenuItem, Select, TextField } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * The fields of a brand product, shared by the Add and Update screens.
 *
 * `pack_size` is free text on purpose: the packs are listed exactly as they read
 * on the artwork — "2.5 kg", "225 g & 500 g", "Small, Medium & Large".
 */
const BrandProductFormFields = ({
  values,
  errors,
  onChange,
  onFile,
  brandOptions,
  existingImage,
  isUpdate,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
        Brand *
      </MDTypography>
      <Select
        fullWidth
        displayEmpty
        value={values.brand || ""}
        onChange={(event) => onChange("brand", event.target.value)}
        sx={{ height: "45px" }}
      >
        <MenuItem value="">Select a brand</MenuItem>
        {brandOptions.map((option) => (
          <MenuItem key={option._id} value={option._id}>
            {option.name}
            {option.group === "range" ? " (range)" : ""}
          </MenuItem>
        ))}
      </Select>
      {errors.brand ? (
        <MDTypography variant="caption" color="error">
          {errors.brand}
        </MDTypography>
      ) : null}
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        required
        label="Product name"
        value={values.name || ""}
        onChange={(event) => onChange("name", event.target.value)}
        error={Boolean(errors.name)}
        helperText={errors.name || 'e.g. "Adult Mini" or "Budgies Food"'}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Variant"
        value={values.variant || ""}
        onChange={(event) => onChange("variant", event.target.value)}
        error={Boolean(errors.variant)}
        helperText={errors.variant || 'Flavour line, e.g. "with Chicken & Turkey"'}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Pack size"
        value={values.pack_size || ""}
        onChange={(event) => onChange("pack_size", event.target.value)}
        error={Boolean(errors.pack_size)}
        helperText={errors.pack_size || 'As printed, e.g. "2.5 kg" or "225 g & 500 g"'}
      />
    </Grid>

    {existingImage ? (
      <Grid item xs={12} sm={4}>
        <MDTypography variant="caption" color="text" display="block" mb={0.5}>
          Current image
        </MDTypography>
        <img
          src={
            existingImage.startsWith("/uploads/")
              ? Constants.imageDomain + existingImage
              : existingImage
          }
          alt={values.name || "Product"}
          style={{
            height: 110,
            maxWidth: "100%",
            objectFit: "contain",
            borderRadius: 6,
            border: "1px solid #e0e0e0",
            background: "#fafafa",
          }}
        />
      </Grid>
    ) : null}

    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        type="file"
        label={existingImage ? "Replace image" : "Product image *"}
        InputLabelProps={{ shrink: true }}
        inputProps={{ accept: "image/*" }}
        onChange={(event) => onFile(event.target.files?.[0] || null)}
        error={Boolean(errors.image)}
        helperText={errors.image || "JPG, PNG or WEBP up to 5 MB"}
      />
    </Grid>

    <Grid item xs={12} sm={2}>
      <TextField
        fullWidth
        type="number"
        label="Display order"
        value={values.sort_order ?? 0}
        onChange={(event) => onChange("sort_order", event.target.value)}
        error={Boolean(errors.sort_order)}
        helperText={errors.sort_order || (isUpdate ? "" : "Left as is, it goes last")}
        inputProps={{ min: 0 }}
      />
    </Grid>

    <Grid item xs={12} sm={2}>
      <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
        Status
      </MDTypography>
      <Select
        fullWidth
        value={values.status || "active"}
        onChange={(event) => onChange("status", event.target.value)}
        sx={{ height: "45px" }}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
      </Select>
    </Grid>

    <Grid item xs={12}>
      <MDBox>
        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
          Description (optional)
        </MDTypography>
        {/* `defaultValue` keeps the editor's own cursor state — see SectionEditor. */}
        <SunEditor
          defaultValue={values.description || ""}
          onChange={(content) => onChange("description", content)}
          setOptions={{
            height: 160,
            buttonList: [
              ["undo", "redo"],
              ["bold", "underline", "italic"],
              ["fontColor", "hiliteColor"],
              ["align", "list"],
              ["link"],
              ["removeFormat"],
            ],
          }}
        />
      </MDBox>
    </Grid>
  </Grid>
);

BrandProductFormFields.defaultProps = {
  errors: {},
  brandOptions: [],
  existingImage: "",
  isUpdate: false,
};

BrandProductFormFields.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onFile: PropTypes.func.isRequired,
  brandOptions: PropTypes.arrayOf(PropTypes.object),
  existingImage: PropTypes.string,
  isUpdate: PropTypes.bool,
};

export default BrandProductFormFields;
